import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import {
    MdChevronRight,
    MdExpandLess,
    MdInstallMobile,
    MdLogout,
    MdOutlineDarkMode,
    MdOutlineLightMode,
    MdOutlineLocalShipping,
    MdOutlineLock,
} from 'react-icons/md'

import { ChangePassword } from '@/Containers/SettingSection/ProfileContainer/Components/UserProfile/ChangePassword'
import { userManager } from '@/Core/Managers'
import { cx } from '@/Ship/Helpers/cssClass'
import { formatPhone } from '@/Ship/Helpers/formatPhone'
import { driverThemeStore } from '@/Ship/Pwa/DriverThemeStore'
import { pwaManager } from '@/Ship/Pwa/PwaManager'
import { unsubscribeFromPush } from '@/Ship/Pwa/ServiceWorkerRegistrar'
import { resolveInitials } from '@/Ship/Templates/Layouts/Components/UserProfileBlock/UserProfileBlock'

import { myShiftStore } from '../Stores/MyShiftStore'
import { formatShiftDay } from '../Stores/MyShiftStore'

import styles from './ProfilePage.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

export const pageConfig: IPageConfig = {
    route: '/driver/profile',
    key: 'Driver.Profile',
    title: 'Профіль',
    template: 'driver',
}

/**
 * Профіль водія: хто я (ПІБ, телефон), поточна зміна, тема, зміна пароля, встановлення
 * застосунку і вихід. Без адмінських полів (e-mail, фото, ролі) — водієві вони не потрібні.
 */
const ProfilePage = observer(() => {
    document.title = 'Профіль'
    const user = (userManager.user ?? {}) as Record<string, any>
    const fullName: string = user['fullName'] || ''
    const phone = user['phone'] ? formatPhone(String(user['phone'])) : ''
    const [passwordOpen, setPasswordOpen] = useState(false)
    const active = myShiftStore.active

    useEffect(() => {
        if (!myShiftStore.loaded) myShiftStore.fetch()
    }, [])

    // Відписуємо пристрій від push ДО виходу, поки токен ще чинний: інакше підписка лишається
    // на сервері за цим водієм, і на спільному телефоні його повідомлення підуть наступному
    const logout = async () => {
        await unsubscribeFromPush()
        userManager.logout()
    }

    return (
        <div className="pwa-page">
            <section className="pwa-card">
                <div className={cx(styles, 'hero')}>
                    <div className={cx(styles, 'avatar')} aria-hidden="true">
                        {user['photo'] ? <img src={user['photo']} alt="" /> : resolveInitials(fullName)}
                    </div>
                    <div className={cx(styles, 'heroMain')}>
                        <span className={cx(styles, 'name')}>{fullName || 'Водій'}</span>
                        {phone && <span className={cx(styles, 'phone')}>{phone}</span>}
                        <span className={cx(styles, 'role')}>Водій</span>
                    </div>
                </div>
            </section>

            <section className="pwa-card">
                <div className={cx(styles, 'row')}>
                    <span className={cx(styles, 'rowIcon')}>
                        <MdOutlineLocalShipping />
                    </span>
                    <div className={cx(styles, 'rowMain')}>
                        <span className={cx(styles, 'rowTitle')}>{active ? 'Поточна зміна' : 'Зміна'}</span>
                        <span className={cx(styles, 'rowHint')}>
                            {active
                                ? `${active.vehicle?.title ?? '—'} · ${formatShiftDay(active.shiftDate)}${active.cluster?.title ? ` · ${active.cluster.title}` : ''}`
                                : myShiftStore.loaded
                                  ? 'Не відкрита'
                                  : 'Завантаження…'}
                        </span>
                    </div>
                </div>

                <div className={cx(styles, 'row')}>
                    <span className={cx(styles, 'rowIcon')}>
                        {driverThemeStore.mode === 'dark' ? <MdOutlineDarkMode /> : <MdOutlineLightMode />}
                    </span>
                    <div className={cx(styles, 'rowMain')}>
                        <span className={cx(styles, 'rowTitle')}>Тема</span>
                        <span className={cx(styles, 'rowHint')}>Світла краще читається на сонці</span>
                    </div>
                    <div className={cx(styles, 'segment')} role="radiogroup" aria-label="Тема">
                        {(['dark', 'light'] as const).map((mode) => (
                            <button
                                key={mode}
                                type="button"
                                role="radio"
                                aria-checked={driverThemeStore.mode === mode}
                                className={`${cx(styles, 'segmentItem')}${driverThemeStore.mode === mode ? ` ${cx(styles, 'segmentItem--active')}` : ''}`}
                                onClick={() => driverThemeStore.setMode(mode)}
                            >
                                {mode === 'dark' ? 'Темна' : 'Світла'}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    className={cx(styles, 'row')}
                    onClick={() => setPasswordOpen((v) => !v)}
                    aria-expanded={passwordOpen}
                >
                    <span className={cx(styles, 'rowIcon')}>
                        <MdOutlineLock />
                    </span>
                    <div className={cx(styles, 'rowMain')}>
                        <span className={cx(styles, 'rowTitle')}>Змінити пароль</span>
                    </div>
                    <span className={cx(styles, 'rowEnd')}>{passwordOpen ? <MdExpandLess /> : <MdChevronRight />}</span>
                </button>
                {passwordOpen && (
                    <div className={cx(styles, 'passwordForm')}>
                        <ChangePassword />
                    </div>
                )}

                {pwaManager.canInstall && (
                    <button type="button" className={cx(styles, 'row')} onClick={() => pwaManager.promptInstall()}>
                        <span className={cx(styles, 'rowIcon')}>
                            <MdInstallMobile />
                        </span>
                        <div className={cx(styles, 'rowMain')}>
                            <span className={cx(styles, 'rowTitle')}>Встановити застосунок</span>
                            <span className={cx(styles, 'rowHint')}>
                                Іконка на головному екрані, працює без браузера
                            </span>
                        </div>
                        <span className={cx(styles, 'rowEnd')}>
                            <MdChevronRight />
                        </span>
                    </button>
                )}
            </section>

            <section className="pwa-card">
                <button type="button" className={`${cx(styles, 'row')} ${cx(styles, 'danger')}`} onClick={logout}>
                    <span className={cx(styles, 'rowIcon')}>
                        <MdLogout />
                    </span>
                    <div className={cx(styles, 'rowMain')}>
                        <span className={`${cx(styles, 'rowTitle')} ${cx(styles, 'danger')}`}>Вийти</span>
                    </div>
                </button>
            </section>
        </div>
    )
})

export default ProfilePage
